import { TraditionalLineItem } from './types';

// The multi-method comparison and the inventory cross-check (program spec §6).
// Pure helpers so every threshold and sentence-driving classification is unit
// tested rather than living inline in ResultsView JSX.

export interface MethodComparison {
  kind: 'measured-lower' | 'equal' | 'traditional-lower';
  /** Always non-negative; the magnitude of the difference. */
  deltaKVA: number;
}

/** Equality within 5 VA — beyond that, one method is genuinely lower. */
export function compareMethods(
  measuredAdjustedKVA: number,
  traditionalDemandKVA: number
): MethodComparison {
  const delta = traditionalDemandKVA - measuredAdjustedKVA;
  if (Math.abs(delta) < 0.005) return { kind: 'equal', deltaKVA: 0 };
  return delta > 0
    ? { kind: 'measured-lower', deltaKVA: delta }
    : { kind: 'traditional-lower', deltaKVA: -delta };
}

export type InventoryBand = 'low' | 'normal' | 'elevated' | 'implausible';

export interface InventoryCheck {
  /** Metered peak ÷ entered connected load. */
  ratio: number;
  /** The same, as a rounded percentage for display. */
  percent: number;
  band: InventoryBand;
}

/**
 * The free physical check the measured path gives the traditional one: the
 * metered peak is a hard lower bound on connected load — a house cannot draw
 * more than it has. Roughly 0.2–0.35 is normal; above ~0.6 the inventory is
 * almost certainly incomplete. Null when either side is missing.
 */
export function inventoryCheck(
  measuredPeakKVA: number,
  connectedLoadKVA: number
): InventoryCheck | null {
  if (!(measuredPeakKVA > 0) || !(connectedLoadKVA > 0)) return null;
  const ratio = measuredPeakKVA / connectedLoadKVA;
  const band: InventoryBand =
    ratio >= 0.6 ? 'implausible' : ratio > 0.35 ? 'elevated' : ratio >= 0.2 ? 'normal' : 'low';
  return { ratio, percent: Math.round(ratio * 100), band };
}

/** Everything the preparer entered, physically connected to the service —
 * including noncoincident-excluded loads and laundry-circuit dryers, which
 * the demand math does not count but the meter certainly can see. */
export function connectedLoadVA(lineItems: TraditionalLineItem[]): number {
  return lineItems.reduce((sum, li) => sum + li.connectedVA, 0);
}

/** Inventory or proposed rows counting at 0 VA — a checked HVAC item with no
 * nameplate entered silently understates the calculated load. */
export function zeroRatedRows(lineItems: TraditionalLineItem[]): TraditionalLineItem[] {
  return lineItems.filter((li) => li.origin !== 'profile' && li.connectedVA === 0);
}

/** The stated reason for the basis of record (design rule 3: a reason, not a
 * preference). Only rendered when more than one method ran. Both citations
 * arrive resolved through the project's edition pack — 220.87 / 220.83 under
 * 2020 and 2023, 120.87 / 120.83 under 2026 — so this sentence never carries
 * a literal section number of its own. */
export function basisReason(
  basis: 'measured' | 'traditional',
  traditionalCitation: string,
  measuredCitation: string
): string {
  return basis === 'measured'
    ? `NEC ${measuredCitation} selected as the basis of record: utility-metered demand data is available for the qualifying period.`
    : `NEC ${traditionalCitation} selected as the basis of record by the preparer; the measured calculation is shown alongside for comparison.`;
}

/** Design rule: the report explains why multiple complete calculations are
 * legitimate. Cites 90.5(B), never 90.4(C) (program spec §1.6). */
export const MULTI_METHOD_REPORT_LINE =
  'NEC 90.5(B) identifies permissive rules as those describing options or alternative methods. Each calculation reported here was performed in full under its own section; no methods were blended. The basis of record is stated above; the authority having jurisdiction approves the method used.';
